import { useCallback, useEffect, useRef, useState } from 'react';
import { ExportEvent, ExportFileStats, ExportState, ProgressState } from '../types';
import { clampPercent, progressByIndex } from '../utils/progressMath';
import { buildExportPayload, postExport } from '../services/exportApi';
import { useCorelExportChannel } from './useCorelExportChannel';
import { terminalLog, sendToTerminal } from '../../../lib/terminalLogger';
import { fromActionError } from '../../../lib/errors';
import { useNotifications } from '../../feedback/NotificationProvider';
import { ErrorCatalog } from '../../feedback/ErrorCatalog';
import { readWithAliases, Keys } from '../../../lib/storage/keys';
import { TokenManager } from '../../../lib/auth/tokenManager';

// Lock global para evitar envio duplicado (ex.: StrictMode em dev monta/desmonta e executa efeitos 2x)
let exportPngLock: { active: boolean; key: string; ts: number } = { active: false, key: '', ts: 0 };

// Helper para modificar exportPngLock de forma atômica (proteção contra race conditions)
function setExportPngLock(value: { active: boolean; key: string; ts: number }): void {
  exportPngLock = value;
}

export interface UseExportProgressArgs {
  onComplete: () => void;
  filePath?: string;
}

export interface UseExportProgressState {
  exportState: ExportState;
  progress: number;
  currentObjectName: string;
  currentMessage: string;
  currentEtapa: string;
  fileStats: ExportFileStats;
  progressState: ProgressState;
  errorMessage: string;
  close: () => void;
}

export function useExportProgress({ onComplete }: UseExportProgressArgs): UseExportProgressState {
  const channel = useCorelExportChannel();
  const { notify } = useNotifications();

  const [exportState, setExportState] = useState<ExportState>('initializing');
  
  // Debug: log whenever exportState changes
  useEffect(() => {
    terminalLog.debug('[EXPORT_PROGRESS] exportState changed', { exportState });
  }, [exportState]);
  const [progress, setProgress] = useState(0);
  const [currentObjectName, setCurrentObjectName] = useState('');
  const [currentMessage, setCurrentMessage] = useState('Preparando exportação...');
  const [currentEtapa, setCurrentEtapa] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [fileStats, setFileStats] = useState<ExportFileStats>({ total: 0, success: 0, failed: 0, files: [] });
  const [progressState, setProgressState] = useState<ProgressState>({
    totalObjetos: 0,
    objetoAtual: 0,
    objetosProcessados: 0,
    percentCompleteFromBackend: 0,
    ultimaAtualizacao: Date.now(),
    statusFinalizado: false,
  });

  const responseReceivedRef = useRef<boolean>(false);
  const requestIdRef = useRef<string>('');
  const lastProgressUpdateRef = useRef<number>(Date.now());
  const progressTimerRef = useRef<number | null>(null);
  const processInitializedRef = useRef(false);
  const processCompletedRef = useRef(false);

  // timers/cleanups
  useEffect(() => {
    // simulação: +0.2% a cada 300ms, sem passar de 99
    progressTimerRef.current = window.setInterval(() => {
      const allowTimerUpdates = processInitializedRef.current && !progressState.statusFinalizado;
      if (allowTimerUpdates && Date.now() - lastProgressUpdateRef.current > 500) {
        setProgress(prev => (prev >= 99 ? prev : Math.min(prev + 0.2, 99)));
      }
    }, 300);
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateProgressByPhase = useCallback((index: number, total: number, phase: 'progress' | 'file' | 'complete') => {
    const pct = phase === 'complete' ? 100 : progressByIndex(index, total, phase);
    setProgress(clampPercent(pct));
    return pct;
  }, []);

  const close = useCallback(() => {
    try {
      const req = { action: 'fechar-documento', data: {} } as const;
      try { sendToTerminal('🚪 [ACTION_SEND] fechar-documento', req); } catch {}
      channel.post(req);
    } catch {}
    onComplete();
  }, [onComplete, channel]);

  // assinatura do canal
  useEffect(() => {
    const unsub = channel.subscribe((parsedResponse: ExportEvent) => {
      // Validação: verificar se parsedResponse é válido antes de processar
      if (!parsedResponse || typeof parsedResponse !== 'object') {
        terminalLog.error('[EXPORT_PROGRESS] parsedResponse inválido recebido do canal', {
          parsedResponseType: typeof parsedResponse
        });
        return;
      }
      
      terminalLog.debug('[EXPORT_PROGRESS] Channel received', { parsedResponse });
      const lastRequestId = requestIdRef.current;
      // Escutar todas as respostas de exportação, não apenas as com requestId específico
      const isOurExport = (
        parsedResponse?.requestId === lastRequestId ||
        parsedResponse?.status === 'progress_update' ||
        parsedResponse?.status === 'complete' ||
        parsedResponse?.status === 'ok' ||
        (parsedResponse?.result && (
          parsedResponse.result.status === 'start' ||
          parsedResponse.result.status === 'progress' ||
          parsedResponse.result.status === 'file_success' ||
          parsedResponse.result.status === 'file_error' ||
          parsedResponse.result.status === 'complete'
        ))
      );
      terminalLog.debug('[EXPORT_PROGRESS] Is our export?', { isOurExport, lastRequestId, received: parsedResponse?.requestId });
      terminalLog.debug('[EXPORT_PROGRESS] Current requestIdRef', { requestIdRef: requestIdRef.current });
      if (!isOurExport) return;
      responseReceivedRef.current = true;
      lastProgressUpdateRef.current = Date.now();

      if (parsedResponse.message) setCurrentMessage(parsedResponse.message);

      if (parsedResponse.status === 'progress_update' && parsedResponse.result) {
        // Normalizar payload que pode vir como array de { Key, Value }
        const normalizeKv = (kv: any) => Array.isArray(kv)
          ? Object.fromEntries(kv.map((p: any) => [p.Key, p.Value]))
          : kv;
        const resultObj: any = normalizeKv(parsedResponse.result);
        const currentObj: any = normalizeKv(resultObj?.current);
        // percent complete pode vir como string/number em result ou current
        const pctRaw = currentObj?.percentComplete ?? currentObj?.percentual ?? resultObj?.percentComplete ?? resultObj?.percentual;
        const pct = typeof pctRaw !== 'undefined' && pctRaw !== null ? Number(pctRaw) : undefined;
        const msgTexto = resultObj?.message ?? resultObj?.texto ?? currentObj?.message ?? currentObj?.texto;
        if (resultObj.status === 'start') {
          // log reduzido: manter apenas logs de requests reais para API
          processInitializedRef.current = true;
          setExportState('exporting');
          if (resultObj.etapa) setCurrentEtapa(resultObj.etapa);
          if (typeof pct === 'number') {
            setProgress(clampPercent(Number(pct)));
          } else {
            setProgress(0);
          }
          if (msgTexto) setCurrentMessage(String(msgTexto));
          setProgressState(prev => ({
            ...prev,
            totalObjetos: Number(resultObj.total ?? 0),
            objetoAtual: 0,
            objetosProcessados: 0,
            percentCompleteFromBackend: (typeof pct === 'number' ? pct : 0) as number,
            ultimaAtualizacao: Date.now()
          }));
        } else if (resultObj.status === 'progress') {
          const idx = Number(currentObj?.index ?? resultObj.index ?? 0) as number;
          const tot = Number(currentObj?.total ?? resultObj.total ?? progressState.totalObjetos ?? 1) as number;
          const name = (currentObj?.name ?? resultObj.name) as string | undefined;
          if (name) setCurrentObjectName(name);
          if (resultObj.etapa) setCurrentEtapa(resultObj.etapa);
          if (msgTexto) setCurrentMessage(String(msgTexto));
          if (typeof pct === 'number') {
            setProgress(clampPercent(Number(pct)));
          } else {
            updateProgressByPhase(idx, tot, 'progress');
          }
          setProgressState(prev => ({
            ...prev,
            totalObjetos: tot || prev.totalObjetos || 0,
            objetoAtual: (idx ?? 0) + 1,
            percentCompleteFromBackend: (typeof pct === 'number' ? pct : prev.percentCompleteFromBackend) as number,
            ultimaAtualizacao: Date.now(),
          }));
        } else if (resultObj.status === 'file_success' || resultObj.status === 'file_error') {
          const idx = Number(resultObj?.index ?? currentObj?.index ?? 0);
          const totSource = (resultObj?.total ?? currentObj?.total ?? progressState.totalObjetos);
          const tot = Number((typeof totSource === 'number' ? totSource : 0) || 1);
          const name = (resultObj?.name ?? currentObj?.name) as string | undefined;
          const path = resultObj?.path;
          const size = resultObj?.size;
          // Evitar duplicação: se já existe na lista com mesmo nome e caminho, não adicionar novamente
          const existsAlready = fileStats.files.some(f => f.name === (name || '') && f.path === path);
          const etapa = resultObj?.etapa;
          if (etapa) setCurrentEtapa(String(etapa));
          setFileStats(prev => ({
            ...prev,
            [resultObj.status === 'file_success' ? 'success' : 'failed']: (resultObj.status === 'file_success' ? prev.success + 1 : prev.failed + 1),
            files: existsAlready ? prev.files : [...prev.files, { name: name || '', status: resultObj.status === 'file_success' ? 'success' : 'error', path, size }]
          }));
          if (typeof pct === 'number') {
            setProgress(clampPercent(Number(pct)));
          } else {
            updateProgressByPhase(idx, tot, 'file');
          }
          setProgressState(prev => ({
            ...prev,
            totalObjetos: tot,
            objetoAtual: 0,
            objetosProcessados: prev.objetosProcessados + 1,
            ultimaAtualizacao: Date.now(),
          }));
        } else if ((resultObj as any)?.status === 'complete') {
          setFileStats(prev => ({
            ...prev,
            total: (resultObj as any).total ?? prev.total,
            success: (resultObj as any).success ?? prev.success,
            failed: (resultObj as any).failed ?? prev.failed,
            successRate: (resultObj as any).success && (resultObj as any).total ? ((resultObj as any).success / (resultObj as any).total) * 100 : prev.successRate,
          }));
          if ((resultObj as any).etapa) setCurrentEtapa((resultObj as any).etapa);
          if (typeof pct === 'number') {
            setProgress(clampPercent(Number(pct)));
          } else {
            updateProgressByPhase((resultObj as any).total ?? 1, (resultObj as any).total ?? 1, 'complete');
          }
          processCompletedRef.current = true;
          // Liberar lock ao finalizar (proteção atômica)
          setExportPngLock({ active: false, key: '', ts: 0 });

          // enviar para API
          try {
            const saved = readWithAliases<any>(Keys.export.currentSaveData, ['currentSaveData'], {
              area: 'session',
              persistIfAliased: true,
            });
            if (saved) {
              const payload = buildExportPayload(
                saved,
                (resultObj as any)?.diretorioEfetivo ?? (resultObj as any)?.directory,
                { exportados: (resultObj as any)?.exportados, diretorioSolicitado: (resultObj as any)?.diretorioSolicitado }
              );
              if (payload) {
                const token = TokenManager.getAccessToken();
                if (!token) {
                  setErrorMessage('Token de autenticação não encontrado');
                } else {
                  const apiStart = performance.now();
                  terminalLog.debug('[EXPORT_PROGRESS] Calling postExport (complete branch)');
                  postExport(payload, token).then(apiRes => {
                    terminalLog.debug('[EXPORT_PROGRESS] API Response (complete)', { apiRes });
                    if (apiRes.success) {
                      terminalLog.debug('[EXPORT_PROGRESS] Setting exportState to success (complete)');
                      setExportState('success');
                      // Após registrar exportação com sucesso na API, aguardar 500ms e fechar documento
                      try {
                        setTimeout(() => {
                          try { sendToTerminal('🚪 [FLOW:EXPORT] API ok → fechar-documento (500ms)'); } catch {}
                          close();
                        }, 500);
                      } catch {}
                    } else {
                      terminalLog.debug('[EXPORT_PROGRESS] API returned success: false (complete)', { message: apiRes.message });
                      if (apiRes.message) setErrorMessage(`Erro ao registrar exportação: ${apiRes.message}`);
                      terminalLog.debug('[EXPORT_PROGRESS] Setting exportState to error');
              setExportState('error');
                    }
                  }).catch(error => {
                    terminalLog.debug('[EXPORT_PROGRESS] postExport promise rejected (complete)', { error });
                    terminalLog.debug('[EXPORT_PROGRESS] Setting exportState to error');
              setExportState('error');
                  });
                }
              }
            }
          } catch (e) {
            terminalLog.error('Erro ao preparar payload de exportação:', e);
          }
        }
      } else if (parsedResponse.status === 'complete' && (parsedResponse as any).result) {
        // Suporte a eventos que chegam com status de topo 'complete'
        const result: any = (parsedResponse as any).result;
        const pct = result?.percentComplete ?? result?.percentual ?? 100;
        setProgress(clampPercent(Number(pct)));
        processCompletedRef.current = true;
        exportPngLock = { active: false, key: '', ts: 0 };
        setExportState('finished');
        if (result?.mensagem) setCurrentMessage(String(result.mensagem));
        try {
          const saved = readWithAliases<any>(Keys.export.currentSaveData, ['currentSaveData'], {
            area: 'session',
            persistIfAliased: true,
          });
          if (saved) {
            const payload = buildExportPayload(
              saved,
              result?.diretorioEfetivo ?? result?.directory,
              { exportados: result?.exportados, diretorioSolicitado: result?.diretorioSolicitado }
            );
            const token = TokenManager.getAccessToken();
            if (payload && token) {
              const apiStart = performance.now();
              postExport(payload, token).then(apiRes => {
                if (apiRes.success) {
                  setExportState('success');
                  try {
                    setTimeout(() => {
                      try { sendToTerminal('🚪 [FLOW:EXPORT] API ok → fechar-documento (500ms)'); } catch {}
                      close();
                    }, 500);
                  } catch {}
                } else {
                  if (apiRes.message) setErrorMessage(`Erro ao registrar exportação: ${apiRes.message}`);
                  terminalLog.debug('[EXPORT_PROGRESS] Setting exportState to error');
              setExportState('error');
                }
              });
            } else {
              terminalLog.debug('[EXPORT_PROGRESS] Setting exportState to error');
              setExportState('error');
            }
          } else {
            setExportState('error');
          }
        } catch (err) {
          terminalLog.error('[EXPORT_PROGRESS] Erro ao processar resposta de exportação', {
            error: err instanceof Error ? err.message : String(err),
            parsedResponseStatus: parsedResponse?.status,
            hasResult: !!(parsedResponse as any)?.result
          });
          setExportState('error');
        }
      } else if (parsedResponse.status === 'ok' && (parsedResponse as any)?.result?.sucesso) {
        // Validação: verificar estrutura de parsedResponse antes de processar
        if (!parsedResponse || typeof parsedResponse !== 'object') {
          terminalLog.error('[EXPORT_PROGRESS] parsedResponse inválido ao processar sucesso', {
            parsedResponseType: typeof parsedResponse
          });
          setExportState('error');
          return;
        }
        
        // Quando não iniciamos a exportação (startExport desabilitado), apenas definir como success
        terminalLog.debug('[EXPORT_PROGRESS] Export completed successfully, setting state to success');
        setExportState('success');
        setCurrentMessage((parsedResponse as any).result?.mensagem || 'Exportação concluída com sucesso!');
        setProgress(100);
        // Liberar lock ao finalizar (proteção atômica)
        setExportPngLock({ active: false, key: '', ts: 0 });
      } else if (parsedResponse.status === 'erro' || ((parsedResponse as any).result && !(parsedResponse as any).result.sucesso)) {
        try {
          const appErr = fromActionError(parsedResponse);
          const entry = ErrorCatalog[appErr.code];
          const message = typeof entry?.message === 'function' ? entry.message({}) : (entry?.message || appErr.message);
          notify({ type: 'error', code: appErr.code, title: entry?.title || 'Exportação falhou', message, sticky: entry?.preferModal });
        } catch {}
        const mensagemErro = (parsedResponse as any).message || (parsedResponse as any).result?.mensagem || 'Erro desconhecido';
        setErrorMessage(mensagemErro);
        setExportState('error');
      }
    });
    return () => { unsub(); };
  }, [channel, onComplete, progressState.totalObjetos, updateProgressByPhase]);

  // timeout de 30s para conclusão parcial
  useEffect(() => {
    const tempoMaximoEspera = 30000;
    const id = window.setTimeout(() => {
      if (!processCompletedRef.current && processInitializedRef.current && progressState.objetosProcessados > 0) {
        setCurrentMessage('Exportação parcialmente concluída. Alguns objetos podem não ter sido processados.');
        setExportState('finished');
      }
    }, tempoMaximoEspera);
    return () => clearTimeout(id);
  }, [onComplete, progressState.objetosProcessados]);

  return {
    exportState,
    progress,
    currentObjectName,
    currentMessage,
    currentEtapa,
    fileStats,
    progressState,
    errorMessage,
    close,
  };
}


